import * as React from 'react';
import { ErrorNotification, SuccessNotification } from '@verclam/toast-v2';

/**
 * `eventName` matches the `type` of the actions dispatched by `Application/Beneficiaries/Documents/**`.
 * Mounted by every screen that fetches or edits a document.
 *
 * Every read failure below stays a silent `ErrorNotification`: the screen it feeds already renders the
 * fresh data, and a successful read never needs an announcement.
 *
 * The three verification gestures (correct, confirm) and the document validation and list item edits each announce their own success —
 * unlike a read, each is a write the reader triggered on purpose, and the card/badge updating on
 * screen is not itself an announcement. Their failures carry the server's own `message`
 * (`ExtractedMessages`), read automatically ahead of this component's static fallback
 * (`guides/notifications.md`, "Logique d'affichage"). `DOCUMENT_TEMPLATE_FILE_FETCH_FAILED` carries no toast: the screen renders its own error state.
 * `DOCUMENT_EXPORTED` carries no toast either:
 * the browser's own download already announces it, same motif as `TRANSCRIPT_EXPORTED`
 * (`InterviewsNotifications`).
 */
const DocumentsNotifications: React.FC = () => {
	return (
		<React.Fragment>
			<ErrorNotification
				eventName="DOCUMENTS_FETCH_FAILED"
				title="Erreur"
				message="Les documents n'ont pas pu être chargés."
			/>
			<ErrorNotification
				eventName="DOCUMENT_FETCH_FAILED"
				title="Erreur"
				message="Le document n'a pas pu être chargé."
			/>
			<SuccessNotification
				eventName="DOCUMENT_FIELD_CONFIRMED"
				title="Information vérifiée"
				message="L'information a été marquée vérifiée."
			/>
			<ErrorNotification
				eventName="DOCUMENT_FIELD_CONFIRMATION_FAILED"
				title="Erreur"
				message="L'information n'a pas pu être marquée vérifiée."
			/>
			<SuccessNotification
				eventName="DOCUMENT_FIELD_CORRECTED"
				title="Correction enregistrée"
				message="La correction a été enregistrée."
			/>
			<ErrorNotification
				eventName="DOCUMENT_FIELD_CORRECTION_FAILED"
				title="Erreur"
				message="La correction n'a pas pu être enregistrée."
			/>
			<SuccessNotification
				eventName="DOCUMENT_VALIDATED"
				title="Document validé"
				message="Le document a été validé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_VALIDATION_FAILED"
				title="Erreur"
				message="Le document n'a pas pu être validé."
			/>
			<SuccessNotification
				eventName="DOCUMENT_LIST_ITEM_ADDED"
				title="Élément ajouté"
				message="L'élément a été ajouté."
			/>
			<ErrorNotification
				eventName="DOCUMENT_LIST_ITEM_ADDITION_FAILED"
				title="Erreur"
				message="L'élément n'a pas pu être ajouté."
			/>
			<SuccessNotification
				eventName="DOCUMENT_LIST_ITEM_REMOVED"
				title="Élément supprimé"
				message="L'élément a été supprimé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_LIST_ITEM_REMOVAL_FAILED"
				title="Erreur"
				message="L'élément n'a pas pu être supprimé."
			/>
			<SuccessNotification
				eventName="DOCUMENT_LIST_ITEM_MOVED"
				title="Élément déplacé"
				message="L'élément a été déplacé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_LIST_ITEM_MOVE_FAILED"
				title="Erreur"
				message="L'élément n'a pas pu être déplacé."
			/>
			<SuccessNotification
				eventName="DOCUMENT_DELETED"
				title="Document supprimé"
				message="Le document a été supprimé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_DELETION_FAILED"
				title="Erreur"
				message="Le document n'a pas pu être supprimé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_EXPORT_FAILED"
				title="Erreur"
				message="Le document n'a pas pu être téléchargé."
			/>
		</React.Fragment>
	);
};

export default DocumentsNotifications;
